"use client";

import React, { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  X,
  Eye,
  Edit,
  Trash2,
} from "lucide-react";
import { getPlatformIcon } from "../utils/platformIcons";
import { useModal } from "../context2/ModalContext";
import dayjs, { Dayjs } from "dayjs";
import TextField from "@mui/material/TextField";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import DeletePostConfirmModal from "../components/modals/DeletePostConfirmModal";

// ---------------- Types ----------------
interface RawPost {
  id: string;
  platform: string;
  content: string;
  status: string;
  campaignId: string;
  scheduledPosts: { scheduledFor: string }[];
  title: string | null; // Add title
  description: string | null;
}

interface FormattedPost {
  id: string;
  date: string;
  time: string;
  content: string;
  platform: string[];
  status: "scheduled" | "draft" | "published" | "failed";
  campaignId: string;
  rawScheduledFor: string;
  title: string | null; // Add title
  description: string | null; // Add description
}

interface CalendarDay {
  date: Date;
  posts: FormattedPost[];
  isCurrentMonth: boolean;
  isToday: boolean;
}

// ---------------- DatePicker Custom Input ----------------
const CustomInput = React.forwardRef<
  HTMLButtonElement,
  { value?: string; onClick?: () => void }
>(({ value, onClick }, ref) => (
  <button
    type="button"
    onClick={onClick}
    ref={ref}
    className="w-full text-left px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
  >
    {value || "Select date & time"}
  </button>
));
CustomInput.displayName = "CustomInput";

// ---------------- Modals (ModalContext) ----------------
const PostPreviewModal = ({
  post,
  onEdit,
  onDelete,
  close,
}: {
  post: FormattedPost;
  onEdit: (
    id: string,
    updates: { content: string; scheduledFor: string; title?: string; description?: string }
  ) => void | Promise<void>;
  onDelete: (id: string) => void | Promise<void>;
  close: () => void;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(post.content);
  const [editedDescription, setEditedDescription] = useState(post.description || "");
  const [editedTitle, setEditedTitle] = useState(post.title || "");  // For title editing
  const [editedDate, setEditedDate] = useState(new Date(post.rawScheduledFor));
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { openModal } = useModal();

  const getStatusColor = (status: string) => {
    switch (status) {
      case "scheduled":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "published":
        return "bg-green-100 text-green-800 border-green-200";
      case "failed":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isSaving) return; // Prevent double submission
    setIsSaving(true);
    setError(null);

    try {
      const updates: any = {
        content: editedContent,
        scheduledFor: editedDate.toISOString(),
      };

      // Only pass title and description if they are edited
      if (editedTitle !== post.title) updates.title = editedTitle;
      if (editedDescription !== post.description) updates.description = editedDescription;

      // Call onEdit with the updates
      await onEdit(post.id, updates);

      // Close the modal after successful save
      close();
    } catch (err) {
      setError("Failed to save changes. Please try again.");
      setIsSaving(false); // Re-enable after error
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedContent(post.content);
    setEditedDescription(post.description || "");
    setEditedTitle(post.title || "");
    setEditedDate(new Date(post.rawScheduledFor));
    setError(null);
  };

  const handleDelete = () => {
    openModal(DeletePostConfirmModal, {
      title: "Delete Post",
      message: "Are you sure you want to delete this post? This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirmAction: async () => {
        try {
          await onDelete(post.id);
          close();
        } catch {
          setError("Failed to delete post. Please try again.");
        }
      },
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white w-full max-w-2xl rounded-md shadow-xl">
          <form className="p-6 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-bold text-lg">Post Details</h3>
              <button
                type="button"
                onClick={close}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
                disabled={isSaving}
              >
                <X size={20} />
              </button>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <div>
              <span
                className={`px-2 py-1 text-xs rounded border ${getStatusColor(post.status)}`}
              >
                {post.status}
              </span>
            </div>

            <div>
              <label className="font-medium block mb-1">Schedule</label>
              {isEditing ? (
                <DateTimePicker
                  label="Select date & time"
                  value={editedDate ? dayjs(editedDate) : null}
                  onChange={(newValue: Dayjs | null) => {
                    if (newValue) setEditedDate(newValue.toDate());
                    else setEditedDate(null);
                  }}
                  minDateTime={dayjs()}
                  minutesStep={5}
                  ampm
                  disabled={isSaving}
                  renderInput={(params) => (
                    <TextField {...params} size="small" />
                  )}
                />
              ) : (
                <p className="text-sm">
                  {editedDate ? dayjs(editedDate).format("YYYY-MM-DD HH:mm") : ""}
                </p>
              )}
            </div>

            {/* Editable Title Field */}
            {post.title && (
              <div>
                <label className="font-medium block mb-1">Edit Title</label>
                {isEditing ? (
                  <textarea
                    rows={2}
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    className="w-full border p-3 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-50"
                    required
                    disabled={isSaving}
                  />
                ) : (
                  <div className="bg-gray-50 p-3 rounded whitespace-pre-wrap">
                    {post.title}
                  </div>
                )}
              </div>
            )}

            {/* Editable Description or Content */}
            <div>
              <label className="font-medium block mb-1">
                {post.description ? "Edit Description" : "Edit Content"}
              </label>

              {isEditing ? (
                post.description ? (
                  <textarea
                    rows={5}
                    value={editedDescription}
                    onChange={(e) => setEditedDescription(e.target.value)}
                    className="w-full border p-3 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-50"
                    required
                    disabled={isSaving}
                  />
                ) : (
                  <textarea
                    rows={5}
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    className="w-full border p-3 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-50"
                    required
                    disabled={isSaving}
                  />
                )
              ) : (
                <div className="bg-gray-50 p-3 rounded whitespace-pre-wrap">
                  {post.description || post.content}
                </div>
              )}
            </div>

            {/* Save/Cancel Buttons */}
            <div className="flex gap-2 pt-4">
              {!isEditing ? (
                <>
                  <button
                    type="button"
                    onClick={() => setIsEditing(true)}
                    className="flex-1 bg-blue-600 text-white py-2 rounded flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors disabled:opacity-50"
                    disabled={isSaving}
                  >
                    <Edit size={16} /> Edit
                  </button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="flex-1 bg-red-600 text-white py-2 rounded flex items-center justify-center gap-2 hover:bg-red-700 transition-colors disabled:opacity-50"
                    disabled={isSaving}
                  >
                    <Trash2 size={16} /> Delete
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleSave}
                    className="flex-1 bg-green-600 text-white py-2 rounded hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Saving...
                      </>
                    ) : (
                      "Save"
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="flex-1 bg-gray-500 text-white py-2 rounded hover:bg-gray-600 transition-colors disabled:opacity-50"
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </form>
        </div>
      </div>
    </LocalizationProvider>
  );
};





const DayPostsModal = ({
  posts,
  date,
  onPostClick,
  close,
}: {
  posts: FormattedPost[];
  date: Date | null;
  onPostClick: (post: FormattedPost) => void;
  close: () => void;
}) => {
  const getModalDateString = () => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  return (
    <div className="bg-white w-full max-w-lg rounded-lg p-4 shadow-xl">
      <div className="flex justify-between mb-3">
        <h3 className="font-semibold">Posts on {getModalDateString()}</h3>
        <button onClick={close}>
          <X size={18} />
        </button>
      </div>

      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {posts.map((post) => {
          const PlatformIcon = getPlatformIcon(post.platform[0] as any);
          return (
            <div
              key={post.id + post.time}
              onClick={() => {
                onPostClick(post);
                close();
              }}
              className="border p-3 rounded cursor-pointer hover:bg-gray-50"
            >
              <div className="flex justify-between items-start text-sm font-medium">
                <div className="flex items-center gap-2">
                  {PlatformIcon && (
                    <PlatformIcon className="w-4 h-4 flex-shrink-0" />
                  )}
                  <span>{post.time}</span>
                </div>
                <Eye size={16} />
              </div>
              <div className="text-sm text-gray-600 truncate mt-1 ml-6">
                {post.title || post.content || "No content"}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------------- Main Component ----------------
export const PostCalendar = ({
  rawScheduledPosts = [],
  onEditPost,
  onDeletePost,
}: {
  rawScheduledPosts?: RawPost[];
  onEditPost: (
    id: string,
    updates: { content: string; scheduledFor: string },
  ) => void | Promise<void>;
  onDeletePost: (id: string) => void | Promise<void>;
}) => {
  const { openModal } = useModal();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const today = new Date();

  const posts: FormattedPost[] = useMemo(() => {
    return rawScheduledPosts.flatMap((raw) =>
      raw.scheduledPosts.map((sp) => {
        const dt = new Date(sp.scheduledFor);

        // Format the date and time
        const year = dt.getFullYear();
        const month = String(dt.getMonth() + 1).padStart(2, "0");
        const day = String(dt.getDate()).padStart(2, "0");
        const dateStr = `${year}-${month}-${day}`;

        const hours = String(dt.getHours()).padStart(2, "0");
        const minutes = String(dt.getMinutes()).padStart(2, "0");
        const timeStr = `${hours}:${minutes}`;

        const metadata = raw.metadata || {};

        // Check if platform is YouTube or TikTok and extract title and description
        let title = null;
        let description = null;
        if (raw.platform === "youtube" || raw.platform === "tiktok") {
          title = metadata.title || null;
          description = metadata.description || null;
        }

        return {
          id: raw.id,
          date: dateStr,
          time: timeStr,
          content: raw.content,
          platform: [raw.platform],
          status: raw.status as FormattedPost["status"],
          campaignId: raw.campaignId,
          rawScheduledFor: sp.scheduledFor,
          title: title, // Assign the title
          description: description, // Assign the description
        };
      }),
    );
  }, [rawScheduledPosts]);

  const getCalendarDays = (): CalendarDay[] => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days: CalendarDay[] = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);

      if (isMobile && date.getMonth() !== month) continue;

      // Use local date format to match the format in posts
      const dateYear = date.getFullYear();
      const dateMonth = String(date.getMonth() + 1).padStart(2, "0");
      const dateDay = String(date.getDate()).padStart(2, "0");
      const dateStr = `${dateYear}-${dateMonth}-${dateDay}`;

      const dayPosts = posts.filter((p) => p.date === dateStr);

      days.push({
        date,
        posts: dayPosts,
        isCurrentMonth: date.getMonth() === month,
        isToday: date.toDateString() === today.toDateString(),
      });
    }
    return days;
  };

  const calendarDays = getCalendarDays();

  const navigateMonth = (dir: "prev" | "next") => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      d.setMonth(prev.getMonth() + (dir === "next" ? 1 : -1));
      return d;
    });
  };

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const getMobileDayClass = (day: CalendarDay) => {
    let classes = "relative ";
    if (day.posts.length > 0) {
      classes +=
        "bg-gradient-to-br from-blue-50 to-blue-100 border-blue-300 shadow-sm ";
    } else {
      classes += "hover:bg-gray-50 ";
    }
    if (day.isToday) {
      classes += "ring-2 ring-blue-400 ring-offset-2 ";
    }
    if (!day.isCurrentMonth) {
      classes += "opacity-40 bg-gray-50 ";
    }
    return classes;
  };

  // (kept as-is, even if unused)
  const getStatusColor = (status: string) => {
    switch (status) {
      case "scheduled":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "published":
        return "bg-green-100 text-green-800 border-green-200";
      case "failed":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const handlePostClick = (post: FormattedPost) => {
    openModal(PostPreviewModal, {
      post,
      onEdit: onEditPost,
      onDelete: onDeletePost,
    });
  };

  const handleDayClick = (dayPosts: FormattedPost[], date: Date) => {
    openModal(DayPostsModal, {
      posts: dayPosts,
      date,
      onPostClick: handlePostClick,
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex gap-2 items-center">
          <Calendar className="text-blue-600" />
          <h2 className="md:text-xl text-base font-semibold">Post Calendar</h2>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => navigateMonth("prev")}
            className="p-0.5 hover:bg-gray-100 rounded"
            type="button"
          >
            <ChevronLeft />
          </button>

          <h3 className="font-semibold min-w-[80px] text-center text-base">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h3>

          <button
            onClick={() => navigateMonth("next")}
            className="p-0.5 hover:bg-gray-100 rounded"
            type="button"
          >
            <ChevronRight />
          </button>
        </div>
      </div>

      {/* WEEKDAYS */}
      <div className="grid grid-cols-7 gap-1">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="text-center text-xs font-medium border-b p-2">
            {d}
          </div>
        ))}

        {/* DAYS */}
        {calendarDays.map((day, i) => (
          <div
            key={i}
            className={`
              border p-2 text-sm flex flex-col transition-all rounded-sm
              ${isMobile ? "h-[80px]" : "h-[70px] md:h-[90px] justify-between"}
              ${!isMobile && !day.isCurrentMonth ? "opacity-30 bg-gray-50" : ""}
              ${!isMobile && day.isToday ? "bg-blue-50 border-blue-400" : ""}
              ${isMobile ? getMobileDayClass(day) : ""}
            `}
          >
            {/* DATE NUMBER */}
            <div
              className={`
                text-xs font-semibold mb-1
                ${isMobile && day.isToday ? "text-blue-600" : ""}
              `}
            >
              {day.date.getDate()}
            </div>

            {/* -------- DESKTOP UI (icons wali) -------- */}
            {!isMobile && day.posts.length > 0 && (
              <div
                onClick={() => handleDayClick(day.posts, day.date)}
                className="cursor-pointer"
              >
                <div className="flex gap-1">
                  <div className="flex items-center gap-1 flex-wrap">
                    {day.posts.slice(0, 4).map((post, idx) => {
                      const Icon = getPlatformIcon(post.platform[0] as any);
                      return <Icon key={idx} className="w-3.5 h-3.5" />;
                    })}

                    {day.posts.length > 4 && (
                      <span className="text-[10px] font-medium text-gray-500">
                        +{day.posts.length - 4}
                      </span>
                    )}
                  </div>
                </div>

                <div className="text-[10px] text-gray-500 mt-1">
                  {day.posts.length} post{day.posts.length > 1 ? "s" : ""}
                </div>
              </div>
            )}

            {/* -------- MOBILE UI (dot wali) -------- */}
            {isMobile && day.posts.length > 0 && (
              <div
                onClick={() => handleDayClick(day.posts, day.date)}
                className="cursor-pointer mt-1 flex flex-col items-center"
              >
                <div className="flex-1 items-center gap-1">
                  <div className="flex -space-x-1">
                    {[...Array(Math.min(day.posts.length, 3))].map((_, idx) => (
                      <div
                        key={idx}
                        className={`
                          w-2 h-2 rounded-full border border-white
                          ${idx === 0 ? "bg-blue-400" : ""}
                          ${idx === 1 ? "bg-blue-500" : ""}
                          ${idx === 2 ? "bg-blue-600" : ""}
                          ${
                            day.posts.length > 3 && idx === 2
                              ? "opacity-50"
                              : ""
                          }
                        `}
                      />
                    ))}
                  </div>

                  <span
                    className={`
                      text-[11px] font-medium
                      ${day.posts.length === 1 ? "text-blue-600" : ""}
                      ${day.posts.length === 2 ? "text-blue-700" : ""}
                      ${day.posts.length === 3 ? "text-blue-800" : ""}
                      ${
                        day.posts.length >= 4
                          ? "text-blue-600 font-semibold"
                          : ""
                      }
                    `}
                  >
                    {day.posts.length}
                    <span className="text-[8px] text-gray-400 ml-0.5">
                      {day.posts.length === 1 ? "post" : "posts"}
                    </span>
                  </span>
                </div>

                {day.posts.length >= 3 && (
                  <div className="w-full h-1 bg-gray-100 rounded-full mt-1 overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full"
                      style={{
                        width: `${Math.min(day.posts.length * 15, 100)}%`,
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
